import { theme } from 'antd';
import { ContactIcon, LoginIcon } from 'assets/images/svg';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPeopleByPersonID } from 'reduxStore/people/selectors';
import { peopleActions } from 'reduxStore/people/slice';
import { ManageLockerPersonDropdown } from 'zComponents/molecules';
import { Col, Collapse, Divider, Drawer, Flex, Row } from 'zui/Antd';
import { InfoTuple } from 'zui/Custom';
import {
	CurrentBookingsContent,
	EditContactInput,
	EditDrawerHeader,
	ErrorPersonContact,
	PanelHeader,
	PersonDrawerSkeleton
} from './components';
import dayjs from 'dayjs';
import AccessLogsContent from './components/AccessLogsContent';
import StatusIcon from 'assets/images/svg/StatusIcon';

const LockersPersonDrawer = ({ personID, visible, setVisible }) => {
	// Theme tokens
	const {
		token: { paddingXXL = 48, borderRadiusLG, paddingMD, marginXS, paddingLG, padding }
	} = theme.useToken();
	const dispatch = useDispatch();

	// Refs
	const currentBookingsRef = useRef(null);
	const accessLogsRef = useRef(null);

	// Redux state

	const person = useSelector((state) => selectPeopleByPersonID(state, personID));
	// Local state
	const [activeKeys, setActiveKeys] = useState(['currentBookings', 'accessLogs']);
	const [isEditingCurrentBooking, setIsEditingCurrentBooking] = useState(false);
	const [isEditingAccessLogs, setIsEditingAccessLogs] = useState(false);
	const [errorContact, setErrorContact] = useState(false);

	// Load person contact info
	useEffect(() => {
		if (visible && personID) {
			dispatch(peopleActions.loadPersonContact({ personID }));
			setErrorContact(false);
		}
	}, [dispatch, personID, visible]);

	// Close drawer
	const handleOnClose = useCallback(() => {
		setVisible(false);
	}, [setVisible]);

	// Toggle panel
	const togglePanel = useCallback((key) => {
		setActiveKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
	}, []);

	// Handle collapse change
	const handleCollapseChange = useCallback(
		(keys) => {
			setActiveKeys(keys);
			if (!keys.includes('currentBookings') && isEditingCurrentBooking) {
				setIsEditingCurrentBooking(false);
			}
			if (!keys.includes('accessLogs') && isEditingAccessLogs) {
				setIsEditingAccessLogs(false);
			}
		},
		[isEditingCurrentBooking, isEditingAccessLogs]
	);

	// Memoize collapse items
	const items = useMemo(
		() => [
			{
				key: 'currentBookings',
				label: (
					<PanelHeader
						title='CURRENT BOOKINGS'
						isExpanded={activeKeys.includes('currentBookings')}
						togglePanel={() => togglePanel('currentBookings')}
					/>
				),
				children: (
					<div ref={currentBookingsRef}>
						<CurrentBookingsContent personID={personID} />
					</div>
				),
				styles: {
					header: {
						marginTop: marginXS
					}
				}
			},
			{
				key: 'divider-1',
				label: <Divider style={{ margin: 0 }} />,
				children: null,
				collapsible: 'disabled',
				showArrow: false,
				style: { padding: 0 }
			},
			{
				key: 'accessLogs',
				label: (
					<PanelHeader
						title='ACCESS LOGS'
						isExpanded={activeKeys.includes('accessLogs')}
						togglePanel={() => togglePanel('accessLogs')}
					/>
				),
				children: (
					<div ref={accessLogsRef}>
						<AccessLogsContent personID={personID} />
					</div>
				),
				styles: {
					header: {
						marginTop: marginXS
					}
				}
			}
		],
		[activeKeys, currentBookingsRef, accessLogsRef, marginXS, personID, togglePanel]
	);

	// Memoize drawer styles
	const drawerStyles = useMemo(
		() => ({
			wrapper: { margin: '30px 20px 30px 0px' },
			content: { borderRadius: borderRadiusLG },
			body: { padding: paddingXXL }
		}),
		[borderRadiusLG, paddingXXL]
	);

	return (
		<Drawer
			placement='right'
			onClose={handleOnClose}
			open={visible}
			width={871}
			closeIcon={false}
			styles={drawerStyles}
		>
			{!person ? (
				<PersonDrawerSkeleton />
			) : (
				<Flex vertical>
					<EditDrawerHeader
						personID={person?.personID}
						displayName={person?.displayName}
						email={person?.email}
						photoUrl={person?.photoUrl}
						fBPersonID={person?.fBPersonID}
					/>
					<div
						style={{
							marginTop: paddingXXL
						}}
					>
						<ManageLockerPersonDropdown personID={person?.personID} />
					</div>

					<Divider style={{ margin: `${paddingMD}px 0px ${paddingMD}px ` }} />

					<Row>
						<Col span={24}>
							<Flex vertical gap={paddingLG} style={{ padding: padding }}>
								<InfoTuple
									icon={<ContactIcon />}
									text='Contact'
									value={
										<EditContactInput
											personID={personID}
											error={errorContact}
											setError={setErrorContact}
										/>
									}
									error={errorContact}
									errorContent={<ErrorPersonContact />}
								/>

								<InfoTuple
									icon={<LoginIcon />}
									text='Last Login'
									value={person?.lastLogin ? dayjs(person.lastLogin).format('D/MM/YY') : '--'}
								/>

								<InfoTuple
									icon={<StatusIcon />}
									text='Status'
									value={person?.isActive ? 'Active' : 'Inactive'}
									highlight={person?.isActive ? 'success' : 'error'}
								/>
							</Flex>
						</Col>
					</Row>

					<Divider style={{ margin: `${paddingMD}px 0px ${paddingMD}px` }} />

					<Collapse
						expandIcon={() => null}
						items={items}
						activeKey={activeKeys}
						onChange={handleCollapseChange}
						ghost
					/>
				</Flex>
			)}
		</Drawer>
	);
};

export default LockersPersonDrawer;
